import { useRef, useState } from "react";
import {
  ActionIcon,
  Group,
  MultiSelect,
  MultiSelectProps,
  Select,
  SelectProps,
} from "@mantine/core";
import { TimeInput } from "@mantine/dates";
import { IconChevronDown, IconChevronUp, IconClock } from "@tabler/icons-react";

import { TodoConstants } from "constant";

const { DAYS, MONTHS, YEARS } = TodoConstants;

// Custom Select with animated chevron
export const ChevronSelect = (props: SelectProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Select
      {...props}
      rightSection={isOpen ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
      onDropdownOpen={() => setIsOpen(true)}
      onDropdownClose={() => setIsOpen(false)}
    />
  );
};

// Custom MultiSelect with animated chevron
export const ChevronMultiSelect = (props: MultiSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <MultiSelect
      {...props}
      rightSection={isOpen ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
      onDropdownOpen={() => setIsOpen(true)}
      onDropdownClose={() => setIsOpen(false)}
    />
  );
};

interface DateTimeSelectProps {
  day: string | null;
  month: string | null;
  year: string | null;
  time: string;
  onDayChange: (value: string | null) => void;
  onMonthChange: (value: string | null) => void;
  onYearChange: (value: string | null) => void;
  onTimeChange: (value: string) => void;
  dayError?: boolean;
  monthError?: boolean;
  yearError?: boolean;
  timeError?: boolean;
  disabled?: boolean;
}

const DateTimeSelect = ({
  day,
  month,
  year,
  time,
  onDayChange,
  onMonthChange,
  onYearChange,
  onTimeChange,
  dayError,
  monthError,
  yearError,
  timeError,
  disabled,
}: DateTimeSelectProps) => {
  const timeRef = useRef<HTMLInputElement>(null);

  return (
    <Group gap="xs">
      <ChevronSelect
        data={DAYS}
        placeholder="1"
        w={70}
        radius="md"
        size="sm"
        value={day}
        onChange={onDayChange}
        error={dayError}
        disabled={disabled}
      />
      <ChevronSelect
        data={MONTHS}
        placeholder="Januar"
        w={120}
        radius="md"
        size="sm"
        value={month}
        onChange={onMonthChange}
        error={monthError}
        disabled={disabled}
      />
      <ChevronSelect
        data={YEARS}
        placeholder={String(new Date().getFullYear())}
        w={80}
        radius="md"
        size="sm"
        value={year}
        onChange={onYearChange}
        error={yearError}
        disabled={disabled}
      />
      <TimeInput
        ref={timeRef}
        value={time}
        onChange={(e) => onTimeChange(e.target.value)}
        w={90}
        radius="md"
        size="sm"
        error={timeError}
        disabled={disabled}
        rightSection={
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={() => timeRef.current?.showPicker()}
            disabled={disabled}
          >
            <IconClock size={16} />
          </ActionIcon>
        }
      />
    </Group>
  );
};

export default DateTimeSelect;
